import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Building2 } from 'lucide-react';
import { UpdateTenantSettingsRequestSchema } from '@ctms/contracts';

import { getApiErrorMessage } from '../../../api/client.js';
import {
  useTenantSettingsQuery,
  useUpdateTenantSettingsMutation,
} from './use-tenant-settings.js';

/** The full-day default used when the site declares no window (D4a). */
const FULL_RANGE_FROM = '00:00';
const FULL_RANGE_TO = '23:45';

export interface SiteStatusCardProps {
  /** Display name of the active site (`tenant.name`). */
  siteName: string | undefined;
  /** Numeric id of the active site (`user.tenantId`). */
  siteId: number | undefined;
  /** `tenant:admin` holders may edit; everyone else reads the window only. */
  canEdit: boolean;
}

interface WorkingHoursFormErrors {
  from?: string;
  to?: string;
  form?: string;
}

/**
 * Strip the `HH:MM` pair from the wire `HH:MM`/`null` pair into editable strings.
 * `null` (no window) becomes an empty field — the user sees "no bound", not "00:00".
 */
function toFieldValue(value: string | null): string {
  return value ?? '';
}

/** An empty field means "no bound" → `null` on the wire (full day for that side). */
function toWireValue(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Site status card of the cockpit dashboard (D4a).
 *
 * Shows the active site plus its working window. The window is validated with the
 * SAME contract schema the API uses, so the on-screen messages can never drift from
 * the server ones. An empty pair is the legacy-friendly full day (00:00–23:45).
 */
export function SiteStatusCard({ siteName, siteId, canEdit }: SiteStatusCardProps): JSX.Element {
  const settingsQuery = useTenantSettingsQuery();
  const updateMutation = useUpdateTenantSettingsMutation();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [errors, setErrors] = useState<WorkingHoursFormErrors>({});
  const [savedNotice, setSavedNotice] = useState(false);

  // Seed the fields from the server once (and after a successful save).
  const serverFrom = settingsQuery.data?.workingHoursFrom ?? null;
  const serverTo = settingsQuery.data?.workingHoursTo ?? null;
  useEffect(() => {
    setFrom(toFieldValue(serverFrom));
    setTo(toFieldValue(serverTo));
  }, [serverFrom, serverTo]);

  const hasWindow = serverFrom !== null && serverTo !== null;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSavedNotice(false);

    const wire = { workingHoursFrom: toWireValue(from), workingHoursTo: toWireValue(to) };
    const parsed = UpdateTenantSettingsRequestSchema.safeParse(wire);
    if (!parsed.success) {
      const next: WorkingHoursFormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === 'workingHoursFrom') {
          next.from = issue.message;
        } else if (field === 'workingHoursTo') {
          next.to = issue.message;
        } else {
          // Object-level rule (od ≤ do) carries no field path.
          next.form = issue.message;
        }
      }
      setErrors(next);
      return;
    }

    setErrors({});
    updateMutation.mutate(parsed.data, {
      onSuccess: () => setSavedNotice(true),
      onError: async (error) => {
        setErrors({ form: await getApiErrorMessage(error) });
      },
    });
  }

  return (
    <section
      aria-label="Status ośrodka"
      className="rounded-lg border border-slate-800 bg-slate-900 p-5 shadow-xs"
    >
      <div className="flex items-center gap-2 mb-3 text-cyan-400">
        <Building2 className="h-4 w-4" />
        <h3 className="text-sm font-semibold text-slate-200">Aktywny Ośrodek</h3>
      </div>
      <p className="text-base font-medium text-slate-100">
        {siteName ?? `Ośrodek #${siteId ?? '—'}`}
      </p>
      <p className="mt-1 text-xs text-slate-400">
        Identyfikator ośrodka: <span className="font-mono text-slate-300">#{siteId ?? '—'}</span>
      </p>

      <div className="mt-4 border-t border-slate-800 pt-3">
        <h4 className="text-xs font-semibold text-slate-300">Godziny pracy</h4>

        {settingsQuery.isPending ? (
          <p data-testid="working-hours-pending" className="mt-2 text-xs text-slate-400">
            Sprawdzanie godzin pracy…
          </p>
        ) : settingsQuery.isError ? (
          <p data-testid="working-hours-error" className="mt-2 text-xs text-amber-400" role="alert">
            Nie udało się pobrać godzin pracy ośrodka.
          </p>
        ) : canEdit ? (
          <form className="mt-2 space-y-2" onSubmit={handleSubmit} noValidate>
            <div className="flex items-end gap-3">
              <label className="flex flex-col gap-1 text-xs text-slate-400">
                Godzina pracy od
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="GG:MM"
                  aria-label="Godzina pracy od"
                  data-testid="working-hours-from"
                  value={from}
                  onChange={(event) => {
                    setFrom(event.target.value);
                    setSavedNotice(false);
                  }}
                  className="w-24 rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-sm text-slate-100 focus:border-cyan-700 focus:outline-none"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-400">
                Godzina pracy do
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="GG:MM"
                  aria-label="Godzina pracy do"
                  data-testid="working-hours-to"
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                    setSavedNotice(false);
                  }}
                  className="w-24 rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-sm text-slate-100 focus:border-cyan-700 focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="rounded-md border border-cyan-800 bg-cyan-950 px-3 py-1.5 text-xs font-medium text-cyan-300 hover:bg-cyan-900 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Zapisywanie…' : 'Zapisz'}
              </button>
            </div>

            {errors.from !== undefined && (
              <p data-testid="working-hours-from-error" role="alert" className="text-xs text-amber-400">
                {errors.from}
              </p>
            )}
            {errors.to !== undefined && (
              <p data-testid="working-hours-to-error" role="alert" className="text-xs text-amber-400">
                {errors.to}
              </p>
            )}
            {errors.form !== undefined && (
              <p data-testid="working-hours-form-error" role="alert" className="text-xs text-amber-400">
                {errors.form}
              </p>
            )}
            {savedNotice && (
              <p data-testid="working-hours-saved" role="status" className="text-xs text-emerald-400">
                Zapisano godziny pracy ośrodka.
              </p>
            )}

            {from.trim() === '' && to.trim() === '' && (
              <p className="text-[11px] text-slate-500">
                Puste pola oznaczają pełny zakres 00:00–23:45 (brak ograniczenia).
              </p>
            )}
          </form>
        ) : (
          <p data-testid="working-hours-readonly" className="mt-2 text-xs text-slate-300">
            {hasWindow ? (
              <>
                od <span className="font-mono text-slate-100">{serverFrom}</span> do{' '}
                <span className="font-mono text-slate-100">{serverTo}</span>
              </>
            ) : (
              <>
                Pełny zakres <span className="font-mono text-slate-100">{FULL_RANGE_FROM}</span>–
                <span className="font-mono text-slate-100">{FULL_RANGE_TO}</span> (brak ograniczenia)
              </>
            )}
          </p>
        )}
      </div>

      <div className="mt-4 rounded border border-emerald-900/60 bg-emerald-950/30 p-2.5 text-xs text-emerald-300">
        Izolacja Row-Level Security (RLS) aktywna. Wszystkie dane operacyjne są chronione przed dostępem z innych placówek.
      </div>
    </section>
  );
}